import { DataConnectionId, DataConnectionType } from "@hex/common";
import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router";
import styled, { useTheme } from "styled-components";

import { HexMenuItem, HexTooltip } from "../../../hex-components/index.js";
import { ORG_ID } from "../../../orgs.js";
import { useDispatch } from "../../../redux/hooks.js";
import { setSearchActive } from "../../../redux/slices/schemaTreeBrowserSlice.js";
import { Routes } from "../../../route/routes.js";
import { ClientCounterEvent } from "../../../util/clientCounterEvent.js";
import { trackCounterEvent } from "../../../util/trackEvent.js";
import { usePinData } from "../../data/schema-browser-v2/usePinData.js";
import { getTableIdPath } from "../../data/schema-browser-v2/utils.js";
import { DataBentoActionsButton } from "../../home/data/DataBentoActionsButton.js";
import { MenuItemTextWithShowOnHover } from "../../home/sidebar/MenuItemTextWithShowOnHover.js";
import { StarActiveIcon, WarehouseTableIcon } from "../../icons/CustomIcons.js";
import { TableListFragment } from "../../sidebar/data-browser/DataBrowserTableList.generated.js";

import { FixedHeightMenuItem } from "./styled.js";
import { isModifiedEvent } from "./utils.js";

export const FavoriteDataMenuItem = styled(MenuItemTextWithShowOnHover)<{
  $useMono?: boolean;
}>`
  font-family: ${({ $useMono = true, theme }) =>
    $useMono ? theme.fontFamily.MONO : theme.fontFamily.DEFAULT};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

interface FavoriteDataTableRowProps {
  table: TableListFragment;
  dataConnectionId: DataConnectionId;
  multiDatabase: boolean;
  connectionType: DataConnectionType;
  counterEventName: ClientCounterEvent;
}

export const FavoriteDataTableRow: React.FunctionComponent<FavoriteDataTableRowProps> =
  React.memo(function FavoriteDataTableRow({
    connectionType,
    counterEventName,
    dataConnectionId,
    multiDatabase,
    table,
  }) {
    const { userActionColor } = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();

    const onClick = useCallback(
      (evt) => {
        trackCounterEvent(counterEventName);
        const withModifier = isModifiedEvent(evt);
        if (withModifier) {
          const url = Routes.DATA_BROWSER.href(ORG_ID, true, {
            tab: "all",
            detailViewType: "table",
            dataSourceId: table.id,
          });
          window.open(url, "_blank");
          return;
        }

        dispatch(
          setSearchActive({
            dataConnectionId,
            searchActive: false,
          }),
        );
        Routes.DATA_BROWSER.push(history, {
          tab: "all",
          detailViewType: "table",
          dataSourceId: table.id,
        });
      },
      [dispatch, history, table.id, dataConnectionId, counterEventName],
    );

    const pinTable = usePinData();

    const onClickRemoveFavorite = useCallback(async () => {
      await pinTable({
        id: table.id,
        idPath: getTableIdPath(table, multiDatabase),
        dataConnectionId: table.dataConnectionId,
        multiDatabase,
        kind: "table",
        newPinnedState: false,
      });
    }, [multiDatabase, pinTable, table]);

    // we should use this state to always show the menu button when it's open
    const [menuIsOpen, setIsMenuOpen] = React.useState(false);

    const unFavoriteMenuItem = useMemo(
      () => (
        <HexMenuItem
          icon={<StarActiveIcon color={userActionColor} />}
          text="Remove favorite"
          onClick={onClickRemoveFavorite}
        />
      ),
      [onClickRemoveFavorite, userActionColor],
    );

    return (
      <HexTooltip content={table.name} targetTagName="div">
        <FixedHeightMenuItem
          icon={<WarehouseTableIcon />}
          text={
            <FavoriteDataMenuItem
              hoverContent={
                <DataBentoActionsButton
                  additionalMenuItem={unFavoriteMenuItem}
                  connectionType={connectionType}
                  isOpen={menuIsOpen}
                  multiDatabase={multiDatabase}
                  setIsOpen={setIsMenuOpen}
                  table={table}
                />
              }
              id={`${table.id}-sidebar-hover-container`}
              text={table.name}
            />
          }
          onClick={onClick}
        />
      </HexTooltip>
    );
  });
